import React, { useEffect, useMemo } from 'react';

import styled from 'styled-components';

// theme
import aampeTheme from '../../constants/aampeTheme';

// hooks
import useActivityLogsQuery from 'hooks/activity-logs/useActivityLogsQuery';
import useHandleQueryParams from 'hooks/helper/useHandleQueryParams';

// constants
import {
    ActivityLogModelType,
    AuditTrailTimeframeOptions,
} from 'models/activty-log.model';

import AuditTrailFilters from '../activity-logs/AuditTrailFilters';
// components
import AuditTrailViewWrapper from '../activity-logs/AuditTrailViewWrapper';

type AuditTrailWrapperProps = {
    formulaId: string;
    hideHeading?: boolean;
};

const AuditTrailWrapper: React.FC<
    React.PropsWithChildren<AuditTrailWrapperProps>
> = ({ formulaId, hideHeading }) => {
    const { data, isLoading } = useActivityLogsQuery({
        target_model: ActivityLogModelType.FORMULA,
        target_id: formulaId.toString(),
    });

    const [queryParams, setQueryParams] = useHandleQueryParams();

    useEffect(() => {
        if (!queryParams.timeframe && !queryParams.person) {
            setQueryParams({
                timeframe: AuditTrailTimeframeOptions.LIFETIME,
                person: 'all',
            });
        }
    }, [queryParams, setQueryParams]);

    const personFilterOptions = useMemo(() => {
        if (!data || data.length === 0) return [];

        const userMap = new Map<number, string>();
        data.forEach((item) => {
            if (!userMap.has(item.user_id)) {
                userMap.set(item.user_id, item.user_name);
            }
        });

        return Array.from(userMap.entries()).map(([user_id, user_name]) => ({
            label: user_name,
            value: user_id,
        }));
    }, [data]);

    return (
        <>
            {!hideHeading && (
                <HeaderContainer>
                    <Heading>Audit Trail</Heading>
                    <Right>
                        <AuditTrailFilters
                            personFilterOptions={personFilterOptions}
                        />
                    </Right>
                </HeaderContainer>
            )}
            {data && (
                <AuditTrailViewWrapper data={data} isLoading={isLoading} />
            )}
        </>
    );
};

export default AuditTrailWrapper;

const HeaderContainer = styled.div({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
});

const Heading = styled.span({
    fontSize: aampeTheme.size.font.heading,
    fontWeight: aampeTheme.weights.medium,
    lineHeight: aampeTheme.size.lineHeight.tall,
    color: aampeTheme.colors.gray800,
});

const Right = styled.div({
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing4(4),
});
